// "Work library" sidebar section — lists the WORK-schema datasets in
// the in-browser DuckDB session, refreshed after every run. Clicking a
// dataset lazily opens the SpreadsheetVisualizer overlay
// (dataset-viewer.ts). Read-only.

import { hasActiveSession, runQuery } from './duckdb';
import { workRelation } from './data-kinds';

interface WorkTable {
  name: string;
  rows: number;
}

let host: HTMLElement | null = null;

export function attachWorkLibrary(el: HTMLElement): void {
  host = el;
  void refreshWorkLibrary();
}

/// Re-query duckdb_tables() and redraw the list. Called after every
/// run / reset and again when a viewer opens. Cheap no-op before the
/// first run: without an active session we render the empty state
/// instead of triggering the multi-MB DuckDB download.
export async function refreshWorkLibrary(): Promise<void> {
  if (!host) return;
  if (!hasActiveSession()) {
    render([]);
    return;
  }
  try {
    const res = await runQuery(
      "SELECT table_name, estimated_size FROM duckdb_tables() WHERE schema_name = 'work' ORDER BY table_name",
    );
    render(res.rows.map((r) => ({ name: String(r[0]), rows: Number(r[1] ?? 0) })));
  } catch {
    // Session mid-reset or query hiccup — show the quiet empty state
    // rather than an error in the sidebar.
    render([]);
  }
}

function render(tables: WorkTable[]): void {
  if (!host) return;
  host.innerHTML = '';

  const header = document.createElement('div');
  header.className = 'work-lib-header';
  header.textContent = tables.length > 0 ? `work library · ${tables.length}` : 'work library';
  host.appendChild(header);

  if (tables.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'work-lib-empty';
    empty.textContent = 'no datasets yet — run a program';
    host.appendChild(empty);
    return;
  }

  const ul = document.createElement('ul');
  ul.className = 'work-lib-list';
  for (const t of tables) {
    const li = document.createElement('li');
    li.className = 'work-lib-item';
    li.tabIndex = 0;
    li.setAttribute('role', 'button');
    li.title = `open work.${t.name} in the dataset viewer`;
    const name = document.createElement('span');
    name.className = 'work-lib-name';
    name.textContent = t.name;
    const rows = document.createElement('span');
    rows.className = 'work-lib-rows';
    rows.textContent = `${t.rows} rows`;
    li.appendChild(name);
    li.appendChild(rows);
    const open = (): void => {
      // Refresh alongside the open so the list reflects any tables a
      // just-finished run added while the sidebar was stale.
      void refreshWorkLibrary();
      void import('./dataset-viewer').then((m) =>
        m.openDatasetViewer({ relation: workRelation(t.name), title: `work.${t.name}` }));
    };
    li.addEventListener('click', open);
    li.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        open();
      }
    });
    ul.appendChild(li);
  }
  host.appendChild(ul);
}
